import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Cropper from 'react-easy-crop'
import styled from 'styled-components'
import { ZoomOut, ZoomIn, RotateCw, X } from 'lucide-react'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import indexPriority from 'theme/z-index'
import { getCroppedImageFile } from 'helpers/cropImage'

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 0.1

/**
 * Modal de recorte de imagem.
 * Recebe um File, devolve um File já recortado no aspecto pedido.
 */
export default function ImageCropperModal({
    file,
    isOpen,
    aspect = 16 / 9,
    cropShape = 'rect',
    outputWidth = 1600,
    title = 'Ajustar imagem',
    description = 'Arraste para reposicionar e use o zoom para enquadrar.',
    confirmLabel = 'Aplicar recorte',
    onConfirm,
    onCancel,
}) {
    const [imageSrc, setImageSrc] = useState(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState(null)
    const dialogRef = useRef(null)
    const previouslyFocused = useRef(null)

    useEffect(() => {
        if (!file || !isOpen) return undefined

        const objectUrl = URL.createObjectURL(file)
        setImageSrc(objectUrl)
        setCrop({ x: 0, y: 0 })
        setZoom(1)
        setRotation(0)
        setError(null)

        return () => URL.revokeObjectURL(objectUrl)
    }, [file, isOpen])

    // Bloqueia o scroll do fundo e devolve o foco ao fechar
    useEffect(() => {
        if (!isOpen) return undefined

        previouslyFocused.current = document.activeElement
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        dialogRef.current?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            previouslyFocused.current?.focus?.()
        }
    }, [isOpen])

    const onCropComplete = useCallback((_, areaPixels) => {
        setCroppedAreaPixels(areaPixels)
    }, [])

    const changeZoom = (delta) => {
        setZoom((current) => {
            const next = Math.round((current + delta) * 100) / 100
            return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
        })
    }

    async function handleConfirm() {
        if (!croppedAreaPixels || isSaving) return
        setIsSaving(true)
        setError(null)
        try {
            const croppedFile = await getCroppedImageFile({
                imageSrc,
                cropPixels: croppedAreaPixels,
                rotation,
                fileName: file?.name,
                mimeType: file?.type,
                maxWidth: outputWidth,
            })
            onConfirm(croppedFile)
        } catch (err) {
            setError('Não foi possível recortar a imagem. Tente novamente.')
        } finally {
            setIsSaving(false)
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            e.stopPropagation()
            onCancel()
        }
    }

    if (!isOpen || !imageSrc) return null

    return createPortal(
        <Overlay onMouseDown={onCancel}>
            <Dialog
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="image-cropper-title"
                aria-describedby="image-cropper-description"
                tabIndex={-1}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                <Header>
                    <div>
                        <DialogTitle id="image-cropper-title">{title}</DialogTitle>
                        <DialogDescription id="image-cropper-description">
                            {description}
                        </DialogDescription>
                    </div>
                    <CloseButton type="button" onClick={onCancel} aria-label="Fechar recorte">
                        <X size={18} />
                    </CloseButton>
                </Header>

                <CropStage>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspect}
                        cropShape={cropShape}
                        // "contain": abre com a imagem inteira visível no tamanho natural
                        // de encaixe; o zoom fica a cargo do usuário.
                        objectFit="contain"
                        showGrid
                        minZoom={MIN_ZOOM}
                        maxZoom={MAX_ZOOM}
                        zoomSpeed={0.2}
                        restrictPosition
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                    />
                </CropStage>

                <Controls>
                    <ZoomControl>
                        <IconButton
                            type="button"
                            onClick={() => changeZoom(-ZOOM_STEP)}
                            disabled={zoom <= MIN_ZOOM}
                            aria-label="Diminuir zoom"
                        >
                            <ZoomOut size={16} />
                        </IconButton>

                        <ZoomSlider
                            id="image-cropper-zoom"
                            type="range"
                            min={MIN_ZOOM}
                            max={MAX_ZOOM}
                            step={0.01}
                            value={zoom}
                            aria-label="Zoom da imagem"
                            aria-valuetext={`${Math.round(zoom * 100)} por cento`}
                            onChange={(e) => setZoom(Number(e.target.value))}
                        />

                        <IconButton
                            type="button"
                            onClick={() => changeZoom(ZOOM_STEP)}
                            disabled={zoom >= MAX_ZOOM}
                            aria-label="Aumentar zoom"
                        >
                            <ZoomIn size={16} />
                        </IconButton>

                        <IconButton
                            type="button"
                            onClick={() => setRotation((r) => (r + 90) % 360)}
                            aria-label="Girar 90 graus"
                        >
                            <RotateCw size={16} />
                        </IconButton>
                    </ZoomControl>

                    {error && <ErrorText role="alert">{error}</ErrorText>}
                </Controls>

                <Footer>
                    <GhostButton type="button" onClick={onCancel} disabled={isSaving}>
                        Cancelar
                    </GhostButton>
                    <PrimaryButton type="button" onClick={handleConfirm} disabled={isSaving}>
                        {isSaving ? 'Processando...' : confirmLabel}
                    </PrimaryButton>
                </Footer>
            </Dialog>
        </Overlay>,
        document.body
    )
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(10, 10, 20, 0.72);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    z-index: ${indexPriority.threeDS};
`;

const Dialog = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 640px;
    max-height: 92vh;
    background: ${colors.neutralLight};
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);

    &:focus {
        outline: none;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 20px 14px;
`;

const DialogTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 4px;
`;

const DialogDescription = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.4;
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: ${colors.neutralDark};
    color: ${colors.nightDark};
    cursor: pointer;
    transition: background .2s ease;

    &:hover {
        background: ${colors.occasionalGrey};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const CropStage = styled.div`
    position: relative;
    width: 100%;
    height: 340px;
    background: #1B1B23;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 420px;
    }

    .reactEasyCrop_CropArea {
        border: 2px solid ${colors.neutralLight};
        box-shadow: 0 0 0 9999em rgba(0, 0, 0, 0.55);
    }
`;

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 20px 4px;
`;

const ZoomControl = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    cursor: pointer;
    transition: all .2s ease;

    &:hover:not(:disabled) {
        border-color: ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
    }

    &:disabled {
        opacity: .45;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const ZoomSlider = styled.input`
    flex: 1;
    min-width: 0;
    accent-color: ${colors.occasionalPurple};
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 4px;
    }
`;

const ErrorText = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 0;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 20px 20px;
`;

const BaseButton = styled.button`
    padding: 11px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;

    &:disabled {
        opacity: .6;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const GhostButton = styled(BaseButton)`
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    color: ${colors.night};

    &:hover:not(:disabled) {
        background: ${colors.neutral};
    }
`;

const PrimaryButton = styled(BaseButton)`
    border: none;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};

    &:hover:not(:disabled) {
        filter: brightness(1.08);
    }
`;
